import { useState } from "react";
import { WorkflowStep } from "@/types/workflow";
import { useWorkflowDocuments } from "@/hooks/useWorkflowDocuments";
import { WorkflowDialogs } from "./workflow/WorkflowDialogs";
import { WorkflowStepsList } from "./workflow/WorkflowStepsList";
import { useWorkflowStatus } from "./workflow/useWorkflowStatus";
import { DocumentGeneratorProvider } from "@/components/documents/DocumentGeneratorProvider";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";

interface WorkflowDisplayProps {
  caseId: string;
  workflow: WorkflowStep[];
  onWorkflowUpdate: () => void;
}

export function WorkflowDisplay({ 
  caseId, 
  workflow, 
  onWorkflowUpdate 
}: WorkflowDisplayProps) {
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | null>(null);
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [showDocumentGenerator, setShowDocumentGenerator] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingStates, setLoadingStates] = useState<{
    aiHelp: { [key: string]: boolean };
    documentGeneration: { [key: string]: boolean };
  }>({
    aiHelp: {},
    documentGeneration: {}
  });
  
  const { data: document, refetch: refetchDocuments } = useWorkflowDocuments(selectedStep);
  const { updating, updateStepStatus } = useWorkflowStatus(caseId, onWorkflowUpdate);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const handleAIHelp = async (step: WorkflowStep) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Ikke logget inn",
        description: "Du må være logget inn for å bruke AI-assistenten.",
        duration: 5000,
      });
      return;
    }

    setLoadingStates(prev => ({
      ...prev,
      aiHelp: { ...prev.aiHelp, [step.title]: true }
    }));

    try {
      setSelectedStep({
        ...step,
        case_id: caseId
      });
      setShowAIHelp(true);
      setError(null);
    } catch (error: any) {
      console.error('Error starting AI help:', error);
      setError("Kunne ikke starte AI-assistenten. Vennligst prøv igjen.");
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke starte AI-assistenten. Vennligst prøv igjen.",
        duration: 5000,
      });
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        aiHelp: { ...prev.aiHelp, [step.title]: false }
      }));
    }
  };

  const handleGenerateDocument = async (step: WorkflowStep) => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Ikke logget inn",
        description: "Du må være logget inn for å generere dokumenter.",
        duration: 5000,
      });
      return;
    }

    setLoadingStates(prev => ({
      ...prev,
      documentGeneration: { ...prev.documentGeneration, [step.title]: true }
    }));

    try {
      setSelectedStep({
        ...step,
        case_id: caseId
      });
      setShowDocumentGenerator(true);
      setError(null);
      await refetchDocuments();
    } catch (error: any) {
      console.error('Error starting document generator:', error);
      setError("Kunne ikke starte dokumentgeneratoren. Vennligst prøv igjen.");
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke starte dokumentgeneratoren. Vennligst prøv igjen.",
        duration: 5000,
      });
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        documentGeneration: { ...prev.documentGeneration, [step.title]: false }
      }));
    }
  };

  return (
    <DocumentGeneratorProvider>
      <div className="flex flex-col h-full min-h-0 bg-background w-full max-w-full">
        <div className={cn(
          "flex-none px-4 py-3 border-b w-full",
          isMobile && "px-3 py-2"
        )}>
          <h3 className={cn(
            "text-lg font-semibold text-center",
            isMobile && "text-base"
          )}>
            Sakens fremdrift
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto overflow-x-hidden w-full max-w-full">
          <div className={cn(
            "px-2 sm:px-4 py-4",
            isMobile && "px-2 py-2"
          )}>
            <div className={cn(
              "space-y-4 pb-16 w-full max-w-full mx-auto",
              isMobile && "space-y-3"
            )}>
              {error && (
                <Alert variant="destructive" className="mx-auto max-w-[95%]">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <WorkflowStepsList
                workflow={workflow}
                caseId={caseId}
                updating={updating}
                onStatusUpdate={(index, newStatus) => updateStepStatus(index, newStatus, workflow)}
                onAIHelp={handleAIHelp}
                onGenerateDocument={handleGenerateDocument}
                loadingStates={loadingStates}
              />
            </div>
          </div>
        </div>

        <WorkflowDialogs
          step={selectedStep}
          showAIHelp={showAIHelp}
          showDocumentGenerator={showDocumentGenerator}
          onAIHelpClose={() => {
            setShowAIHelp(false);
            setSelectedStep(null);
            setError(null);
          }}
          onDocumentGeneratorClose={() => {
            setShowDocumentGenerator(false);
            setSelectedStep(null);
            setError(null);
          }}
          documents={document ? [document] : []}
        />
      </div>
    </DocumentGeneratorProvider>
  );
}
